import React, { FC, HTMLAttributes, useEffect, useState } from 'react'
import { Counter, ImageBox } from '@engyalo/storefront-components'
import { Button, Box, Heading, Text } from '@chakra-ui/core'
import { Price } from '../Price'
import 'lazysizes'
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil'
import { useTranslation } from 'react-i18next'
import { Hook, IStorefrontSettings } from '@engyalo/storefront-types'
import { ICartDictionary, IProduct } from '@engyalo/storefront-types/lib/plugins/model'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import { removeProductFromCartStart, addProduct, removeProduct, updateQuantity } from '@engyalo/storefront-events'
import { cartState, configState, orderConfigState } from '../../recoil/atoms'
import { generateCartId } from '../../lib/cartIdGenerator'
import { getTotalPrice } from '../../lib/getTotalPrice'
import Plugin, { usePlugin } from '../../Plugin'
import { getActionScreen } from '../../utils/analytics'
import { useLocation } from 'react-router'
import { skipIfInPreviewMode } from '../../utils/mode'
import { useUnitTypes } from '../../hooks/useUnitTypes'

interface IProps extends HTMLAttributes<HTMLDivElement> {
  product: IProduct
  deleteItem: (product: IProduct) => void
}

export const CartItem: FC<IProps> = ({ product, deleteItem }: IProps) => {
  const location = useLocation()
  const { t } = useTranslation()
  const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
  const config = useRecoilValue(configState)
  const storefrontPlugin = usePlugin(Hook.STOREFRONT)
  const totalPrice = getTotalPrice(product)
  const [cart, setCart] = useState<ICartDictionary>({});
  const setCartState = useSetRecoilState<ICartDictionary>(cartState)
  const { unitType, hasUnitTypes } = useUnitTypes(cart, product)

  const firstCartLoad = useRecoilCallback(({snapshot}) => async () => {
    const atomCart = await snapshot.getPromise(cartState);
    const tempCart =  { ...atomCart }
    setCart(tempCart)
    setCartState(tempCart)
  })

  useEffect(() => {
    firstCartLoad()
  }, [])

  let hidePrices: boolean
  if ((storefrontPlugin?.props as IStorefrontSettings)?.hidePrices == undefined) {
    hidePrices = totalPrice <= 0
  } else {
    hidePrices = (storefrontPlugin?.props as IStorefrontSettings)?.hidePrices ?? false
  }

  const session = {
    userID: orderConfig.userID || '',
    customer: config.customer,
    sessionID: orderConfig.id || '',
  }

  const getProductFirstImage = (product: IProduct): string => {
    if (product && product.imageURL) return product.imageURL[0] || ''
    return ''
  }

  const addToCartAsync = useRecoilCallback(({snapshot}) => async (product: IProduct, quantity : number, shouldReplace : boolean) => {
    const atomCart = await snapshot.getPromise(cartState);
    const tempCart =  { ...atomCart }

    if (product && product.id) {
      const currentSku = generateCartId(product)
      if (tempCart[currentSku]) {
        const tempProd = { ...tempCart[currentSku] }
        if (tempProd.quantity) {
          tempProd.quantity = shouldReplace ? quantity : tempProd.quantity + quantity
          if (tempProd.quantity > 0) {
            tempCart[currentSku] = {
              ...tempProd,
              totalPrice,
            }
            setCart(tempCart)
            setCartState(tempCart)
          } else {
            deleteItem(tempCart[currentSku])
          }
        }
      } else if (quantity > 0) {
        tempCart[currentSku] = {
          ...product,
          totalPrice,
          quantity,
          cartId: currentSku,
        } as IProduct
      }
      setCart(tempCart)
      setCartState(tempCart)
    }
  })

  if (!product) {
    return null
  }

  return (
    <Box borderBottom="1px solid" borderColor="gray.200" display="flex" padding="8px" data-qa-node="cartitem-product-container">
      <Box flex="2" display="flex">
        <Box display="flex">
          <Box w="4rem !important" display="flex" data-qa-node="cartitem-product-image">
            <ImageBox imageUrl={getProductFirstImage(product)} />
          </Box>
          <Box paddingLeft="8px" display="flex" flexDirection="column">
            <Heading marginY="4px" as="h3" size="sm" flex="1" data-qa-node="cartitem-product-name">
              {product.name}
            </Heading>
            <Box fontSize="xs" fontFamily="body" color="gray.100" paddingBottom="4px" display="flex" flexDir="column" data-qa-node="cartitem-product-description">
              <Text>{product.description}</Text>
              {hasUnitTypes && <Text>{unitType?.type}</Text>}
            </Box>
            <Plugin hook={Hook.CART_ITEM_DETAIL} pluginProps={{ cart, product, tFunction: t }}>
              <></>
            </Plugin>
            {!hidePrices && (
              <Price
                price={product.totalPrice ? product.totalPrice : product.price}
                currency={config.currencyPrefix}
                currencyFormat={config.currencyFormat}
              />
            )}
            <Button
              data-qa-node="cartitem-delete-button"
              width="4.2rem"
              color="red.500"
              variant="link"
              border="transparent"
              fontSize="sm"
              textDecor="underline"
              backgroundColor="transparent"
              fontWeight="normal"
              onClick={(): void => {
                skipIfInPreviewMode(() => {
                  removeProductFromCartStart({
                    product: {
                      id: product.id || '',
                      sku: product.sku || '',
                      name: product.name || '',
                      category: product.category || '',
                      price: product.price || 0,
                      totalPrice: product.price && product.quantity ? product.price * product.quantity : 0,
                    },
                    session: {
                      userID: orderConfig.userID || '',
                      customer: config.customer,
                      sessionID: orderConfig.id || '',
                    },
                  })
                })
                deleteItem(product)
              }}
            >
              {t('cartItem-delete')}
            </Button>
          </Box>
        </Box>
      </Box>
      <Box flex="1" data-qa-node="cartitem-counter">
        <Counter
          maxValue={config.maxQuantity}
          name={product.cartId || ''}
          addAction={(): void => {
            skipIfInPreviewMode(() => {
              addProduct({
                product: {
                  id: product.id || '',
                  sku: product.sku || '',
                  name: product.name || '',
                  category: product.category || '',
                  price: product.price || 0,
                  note: '',
                  screen: getActionScreen(location),
                },
                session,
              })
            })
            addToCartAsync(product, unitType!.interval, false)
          }}
          minusAction={(): void => {
            skipIfInPreviewMode(() => {
              removeProduct({
                product: {
                  id: product.id || '',
                  sku: product.sku || '',
                  name: product.name || '',
                  category: product.category || '',
                  price: product.price || 0,
                  note: '',
                  screen: getActionScreen(location),
                },
                session,
              })
            })
            addToCartAsync(product, -unitType!.interval, false)
          }}
          onInputBlur={(event: React.FocusEvent<HTMLInputElement>): void => {
            const quantity = parseInt(event.target.value)
            skipIfInPreviewMode(() => {
              updateQuantity({
                product: {
                  id: product.id || '',
                  sku: product.sku || '',
                  name: product.name || '',
                  category: product.category || '',
                  price: product.price || 0,
                  note: '',
                  quantity,
                  screen: getActionScreen(location),
                },
                session,
              })
            })
            addToCartAsync(product, quantity, true)
          }}
          value={cart[product.cartId || ''] ? cart[product.cartId || ''].quantity : 0}
          format={unitType?.format}
        />
      </Box>
    </Box>
  )
}
